import type { IntentName, IntentMatch, ChatAttachment } from './types';

interface IntentPattern {
  pattern: RegExp;
  weight: number;
}

const INTENT_KEYWORDS: Record<Exclude<IntentName, 'fallback'>, IntentPattern[]> = {
  greeting: [
    { pattern: /^(hi|hello|hey|howdy|good morning|good afternoon|good evening)\b/i, weight: 1.0 },
    { pattern: /\b(help me|can you help|what can you do|assist)\b/i, weight: 0.5 },
  ],
  bot_status: [
    { pattern: /\b(agent|bot|automation)s?\b.*\b(status|state|running|stopped|health|doing)\b/i, weight: 0.9 },
    { pattern: /\bhow\b.*\bmy\b.*\bbots?\b/i, weight: 0.8 },
    { pattern: /\bbots?\b.*\bdoing\b/i, weight: 0.7 },
  ],
  bot_list: [
    { pattern: /\b(list|show|all|my)\b.*\b(agents?|bots?|automations?)\b/i, weight: 0.9 },
    { pattern: /\bhow many bots?\b/i, weight: 0.8 },
    { pattern: /\bwhich bots?\b/i, weight: 0.7 },
  ],
  bot_diagnose: [
    { pattern: /\b(agent|bot|automation)s?\b.*\b(not working|stopped|error|failed|issue|problem|crashed|down)\b/i, weight: 0.95 },
    { pattern: /\btroubleshoot\b/i, weight: 0.8 },
    { pattern: /\bwhy\b.*\b(stopped|failed|crashed|not working)\b/i, weight: 0.85 },
    { pattern: /\bdiagnos/i, weight: 0.8 },
  ],
  bot_create_help: [
    { pattern: /\b(create|new|setup|configure|build|make|deploy)\b.*\b(agent|bot|automation)\b/i, weight: 0.9 },
    { pattern: /\bhow\b.*\bstart\b.*\btrading\b/i, weight: 0.7 },
    { pattern: /\bset\s*up\b.*\bbot\b/i, weight: 0.85 },
  ],
  agent_workflow: [
    { pattern: /\b(draft|launch|create|prepare)\b.*\b(supervised\s+)?agent\b/i, weight: 0.96 },
    { pattern: /\b(agent|proposal|mandate)\b.*\b(gate|policy|evidence|safe action|control)\b/i, weight: 0.92 },
    { pattern: /\b(pending approvals?|policy-gated|decision queue)\b/i, weight: 0.88 },
  ],
  recent_orders: [
    { pattern: /\b(recent|last|latest)\b.*\b(orders?|trades?)\b/i, weight: 0.9 },
    { pattern: /\btrade\b.*\bhistory\b/i, weight: 0.8 },
    { pattern: /\border\b.*\b(status|history|list)\b/i, weight: 0.7 },
    { pattern: /\bshow\b.*\borders?\b/i, weight: 0.7 },
  ],
  positions: [
    { pattern: /\b(open|current|active)\b.*\bpositions?\b/i, weight: 0.9 },
    { pattern: /\bpositions?\b/i, weight: 0.6 },
    { pattern: /\bwhat\b.*\b(holding|hold)\b/i, weight: 0.7 },
    { pattern: /\bshow\b.*\bpositions?\b/i, weight: 0.8 },
  ],
  portfolio_overview: [
    { pattern: /\b(portfolio|equity|balance|pnl|profit|loss|performance)\b/i, weight: 0.7 },
    { pattern: /\bhow\b.*\b(performing|performance)\b/i, weight: 0.7 },
    { pattern: /\b(sharpe|drawdown|var)\b/i, weight: 0.7 },
    { pattern: /\bportfolio\b.*\b(overview|summary)\b/i, weight: 0.9 },
  ],
  risk_help: [
    { pattern: /\brisk\b.*\b(manage|setting|config|policy|parameter)\b/i, weight: 0.9 },
    { pattern: /\b(stop[\s-]?loss|position[\s-]?siz|max[\s-]?drawdown)\b/i, weight: 0.85 },
    { pattern: /\brisk management\b/i, weight: 0.9 },
  ],
  strategy_help: [
    { pattern: /\b(strategy|strategies)\b/i, weight: 0.6 },
    { pattern: /\b(grid|momentum|arbitrage|mean[\s-]?reversion|dca)\b.*\b(trading|strategy)?\b/i, weight: 0.8 },
    { pattern: /\bwhich strategy\b/i, weight: 0.8 },
    { pattern: /\bcompare\b.*\bstrateg/i, weight: 0.8 },
  ],
  strategy_action: [
    { pattern: /\b(recommend|select|rank)\b.*\bstrateg/i, weight: 0.94 },
    { pattern: /\b(convert|draft)\b.*\bstrategy\b.*\bagent\b/i, weight: 0.96 },
    { pattern: /\bstrategy\b.*\b(tradeoffs?|candidate|catalog)\b/i, weight: 0.9 },
    { pattern: /\b(use|prepare)\b.*\b(this\s+)?strategy\b/i, weight: 0.93 },
    { pattern: /\b(strategy)\b.*\b(fit|risk|compatible|parameters?)\b/i, weight: 0.9 },
  ],
  backtest_help: [
    { pattern: /\b(backtest|back[\s-]?test)\b/i, weight: 0.9 },
    { pattern: /\btest\b.*\bstrategy\b/i, weight: 0.7 },
    { pattern: /\bhistorical\b.*\btest/i, weight: 0.7 },
  ],
  backtest_action: [
    { pattern: /\b(interpret|compare|explain)\b.*\b(backtests?|selected runs?)\b/i, weight: 0.97 },
    { pattern: /\b(draft|create)\b.*\bagent\b.*\b(backtest|best run)\b/i, weight: 0.97 },
    { pattern: /\b(configure|start|prepare)\b.*\b(new\s+)?backtest\b/i, weight: 0.94 },
    { pattern: /\b(backtest|simulate)\b.*\b(this\s+)?strategy\b/i, weight: 0.94 },
  ],
  connect_exchange: [
    { pattern: /\b(connect|add|setup|link)\b.*\b(exchange|venue|api[\s-]?key)\b/i, weight: 0.9 },
    { pattern: /\b(binance|coinbase|kraken|bybit|okx|kucoin)\b/i, weight: 0.6 },
    { pattern: /\bapi[\s-]?key\b.*\b(setup|add|create)\b/i, weight: 0.8 },
  ],
  connection_audit: [
    { pattern: /\b(audit|check|validate)\b.*\b(connection|credential|venue|api[\s-]?key|posture)\b/i, weight: 0.96 },
    { pattern: /\b(stale|missing|permission|scope)\b.*\b(connection|credential|venue|api[\s-]?key)\b/i, weight: 0.92 },
    { pattern: /\bvenue access\b.*\bagent/i, weight: 0.9 },
  ],
  explain_error: [
    { pattern: /\b(error|fail|issue|problem|bug)\b/i, weight: 0.4 },
    { pattern: /\bwhat\b.*\bhappened\b/i, weight: 0.5 },
    { pattern: /\bexplain\b.*\b(error|this)\b/i, weight: 0.7 },
    { pattern: /GW-[A-Z]+-\d{3}/i, weight: 0.95 },
  ],
  explain_notification: [
    { pattern: /\b(notification|alert)\b.*\b(mean|explain|about|what)\b/i, weight: 0.9 },
    { pattern: /\bexplain\b.*\b(notification|alert)\b/i, weight: 0.9 },
  ],
  monitoring_triage: [
    { pattern: /\b(triage|diagnose|investigate)\b.*\b(incidents?|alerts?|health|monitoring|queue)\b/i, weight: 0.96 },
    { pattern: /\b(restart checklist|health sweep|system health)\b/i, weight: 0.93 },
    { pattern: /\b(memory|network|queue)\b.*\b(high|elevated|warning|alert)\b/i, weight: 0.9 },
  ],
  compliance_help: [
    { pattern: /\b(compliance|regulation|kyc|aml|kyt)\b/i, weight: 0.8 },
    { pattern: /\bnon[\s-]?custod/i, weight: 0.7 },
    { pattern: /\baudit\b.*\b(trail|log)\b/i, weight: 0.7 },
    { pattern: /\bkill[\s-]?switch\b/i, weight: 0.7 },
  ],
  compliance_review: [
    { pattern: /\b(prepare|review|summarize)\b.*\b(compliance|audit|alert)\b/i, weight: 0.96 },
    { pattern: /\b(acknowledgement|acknowledge|rationale)\b/i, weight: 0.92 },
    { pattern: /\b(audit trail|control evidence|aml|kyt)\b.*\b(review|gap|packet)\b/i, weight: 0.94 },
  ],
  tax_reconcile: [
    { pattern: /\b(resolve|review|explain|prepare)\b.*\b(tax|reconciliation|lot|fx|taxable|export)\b/i, weight: 0.96 },
    { pattern: /\b(lot attribution|manual transfer|taxable events?|tax export|fx reference)\b/i, weight: 0.94 },
  ],
  report_analysis: [
    { pattern: /\b(explain|find|draft|summarize)\b.*\b(p&l|pnl|report|analytics|performance|committee)\b/i, weight: 0.95 },
    { pattern: /\b(underperforming agents?|p&l drivers?|risk-adjusted returns?)\b/i, weight: 0.93 },
  ],
  workspace_action: [
    { pattern: /\b(prepare|launch|run|start|open|create|export|refresh|sync|rotate|filter|sort|configure|cleanup|validate|review)\b.*\b(action|plan|checklist|tenant|user|api key|setting|notification|status|diagnostic|kpi|export|log|health|slug|membership)\b/i, weight: 0.94 },
    { pattern: /\b(create tenant|add user|rotate key|audit key|export tenant|manage kpis?|system health|audit logs?|validate tenant|tenant slug)\b/i, weight: 0.96 },
    { pattern: /\b(parse|simulate|stage)\b.*\b(console|command|ticket)\b/i, weight: 0.96 },
    { pattern: /\b(command palette|pre-trade guardrails?|console audit trail)\b/i, weight: 0.94 },
    { pattern: /\b(edit|save|update|prepare)\b.*\b(profile|notification preferences?|account)\b/i, weight: 0.94 },
    { pattern: /\b(revoke|end)\b.*\b(session)\b/i, weight: 0.94 },
    { pattern: /\b(create|publish|archive|unarchive|restore|filter|refresh)\b.*\b(strategy catalog|catalog entry|strategy entry)\b/i, weight: 0.94 },
    { pattern: /\b(review|summarize|prepare)\b.*\b(user access|organization memberships?|role change)\b/i, weight: 0.94 },
    { pattern: /\b(internal checks?|security checks?|csp|https|consent|demo badge)\b/i, weight: 0.9 },
    { pattern: /\b(what can you do here|help me here|what actions can you take here)\b/i, weight: 0.98 },
    { pattern: /\b(show|list|what are)\b.*\b(available assistant actions|page actions|actions on this page)\b/i, weight: 0.98 },
  ],
  kb_search: [
    { pattern: /\b(how|what|when|where|why|can|does)\b/i, weight: 0.15 },
  ],
};

// ---- Entity extraction ----

const ENTITY_PATTERNS = {
  botName: /\bbot\s+["']?([A-Za-z0-9_ -]{2,30})["']?/i,
  botId: /\b([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\b/i,
  exchange: /\b(binance|coinbase|kraken|bybit|okx|kucoin)\b/i,
  errorCode: /\b(GW-[A-Z]+-\d{3})\b/i,
  tradingPair: /\b([A-Z]{2,10})[/\-_]([A-Z]{2,10})\b/,
};

function extractEntities(input: string): IntentMatch['extractedEntities'] {
  const entities: IntentMatch['extractedEntities'] = {};

  const botNameMatch = input.match(ENTITY_PATTERNS.botName);
  if (botNameMatch) entities.botName = botNameMatch[1].trim();

  const botIdMatch = input.match(ENTITY_PATTERNS.botId);
  if (botIdMatch) entities.botId = botIdMatch[1];

  const exchangeMatch = input.match(ENTITY_PATTERNS.exchange);
  if (exchangeMatch) entities.exchange = exchangeMatch[1].toLowerCase();

  const errorCodeMatch = input.match(ENTITY_PATTERNS.errorCode);
  if (errorCodeMatch) entities.errorCode = errorCodeMatch[1];

  const pairMatch = input.match(ENTITY_PATTERNS.tradingPair);
  if (pairMatch) entities.tradingPair = `${pairMatch[1]}/${pairMatch[2]}`;

  return entities;
}

// ---- Intent classification ----

const MIN_CONFIDENCE = 0.3;

export function classifyIntent(
  input: string,
  attachments?: ChatAttachment[],
): IntentMatch[] {
  const normalized = input.trim().toLowerCase();
  const entities = extractEntities(input);

  const scores = new Map<IntentName, number>();

  for (const [intent, patterns] of Object.entries(INTENT_KEYWORDS)) {
    let bestScore = 0;
    for (const { pattern, weight } of patterns) {
      if (pattern.test(normalized)) {
        bestScore = Math.max(bestScore, weight);
      }
    }
    if (bestScore > 0) {
      scores.set(intent as IntentName, bestScore);
    }
  }

  // Boost explain_error if error context attachment present
  if (attachments?.some(a => a.type === 'error_context')) {
    const current = scores.get('explain_error') ?? 0;
    scores.set('explain_error', Math.min(current + 0.5, 1.0));
  }

  // Build sorted results
  const results: IntentMatch[] = Array.from(scores.entries())
    .filter(([, score]) => score >= MIN_CONFIDENCE)
    .sort(([, a], [, b]) => b - a)
    .map(([intent, confidence]) => ({
      intent,
      confidence,
      extractedEntities: entities,
    }));

  // Always include fallback at the end
  if (results.length === 0) {
    results.push({
      intent: 'fallback',
      confidence: 0,
      extractedEntities: entities,
    });
  }

  return results;
}
